<template>
  <bg-modal
    :value="value"
    :title="title"
    :body-scroll="false"
    desktop-size="lg"
    @close="close"
  >
    <bg-text
      v-if="fileName"
      size="body-2"
      class="bg-u-text-neutral-700 bg-u-mb-md"
    >
      {{ fileName }}
    </bg-text>

    <bg-text v-if="error" size="body-2" class="bg-u-text-danger">
      {{ error }}
    </bg-text>

    <div v-else class="csv-preview-modal__table">
      <bg-table :columns="columns" :rows="rows" :loading="isLoading" />
    </div>
  </bg-modal>
</template>

<script>
import { ref, watch } from '@vue/composition-api';
import { BgModal, BgText, BgTable } from 'bangul-vue';

import { parseCsv } from './parseCsv';

export default {
  name: 'CsvPreviewModal',

  components: {
    BgModal,
    BgText,
    BgTable,
  },

  props: {
    /**
     * Controls modal visibility. Supports `v-model`.
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * The CSV File to preview. Parsed lazily when the modal opens.
     */
    file: {
      type: File,
      default: null,
    },
    /**
     * Modal heading.
     */
    title: {
      type: String,
      default: 'Preview File',
    },
  },

  setup(props, { emit }) {
    const isLoading = ref(false);
    const fileName = ref('');
    const columns = ref([]);
    const rows = ref([]);
    const error = ref('');

    const readFileAsText = file =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });

    /*
     * Map the parsed CSV onto BgTable's shape. Index-based column keys keep us
     * safe against duplicate or empty header labels in the uploaded file.
     */
    const toTableData = ({ headers, rows: dataRows }) => ({
      columns: headers.map((label, index) => ({ key: `col${index}`, label })),
      rows: dataRows.map(cells =>
        cells.reduce(
          (row, cell, index) => ({ ...row, [`col${index}`]: cell }),
          {}
        )
      ),
    });

    const loadPreview = async () => {
      if (!props.file) return;

      isLoading.value = true;
      fileName.value = props.file.name;
      columns.value = [];
      rows.value = [];
      error.value = '';

      try {
        const { headers, rows: parsedRows } = parseCsv(
          await readFileAsText(props.file)
        );

        if (!headers.length) {
          error.value = 'File CSV kosong.';
        } else {
          const tableData = toTableData({ headers, rows: parsedRows });
          columns.value = tableData.columns;
          rows.value = tableData.rows;
        }
      } catch {
        error.value = 'Gagal membaca file. Silakan coba lagi.';
      } finally {
        isLoading.value = false;
      }
    };

    watch(
      () => props.value,
      isOpen => {
        if (isOpen) loadPreview();
      },
      { immediate: true }
    );

    const close = () => {
      emit('input', false);
      emit('close');
    };

    return {
      isLoading,
      fileName,
      columns,
      rows,
      error,
      close,
    };
  },
};
</script>

<style lang="scss" scoped>
.csv-preview-modal {
  &__table {
    max-height: 60vh;
    overflow: auto;
  }
}
</style>
